import React, { useState, useEffect } from "react";
import {
  ActivityDefinition,
  Form,
  User,
  GoogleDriveAttachment,
} from "../../types";
import { FormLibrary } from "../forms/FormLibrary";
import { FormViewer } from "../forms/FormViewer";
import FormEditor from "../forms/FormEditor";
import { GoogleDriveFilePicker } from "../workflow/GoogleDriveFilePicker";
import {
  PlusIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  XMarkIcon,
  DocumentTextIcon,
  EyeIcon,
  UserIcon,
  PencilIcon,
  TrashIcon,
  PaperClipIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";
import toast from "../../utils/toast";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { apiService } from "../../services/apiService";

interface ActivityEditorProps {
  activity?: ActivityDefinition | null;
  onSave: (activity: ActivityDefinition) => void;
  onCancel: () => void;
  readonly?: boolean;
}

export const ActivityEditor: React.FC<ActivityEditorProps> = ({
  activity,
  onSave,
  onCancel,
  readonly = false,
}) => {
  const { t } = useTranslation(["activities", "common", "forms"]);
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    startDate: "",
    endDate: "",
    requiresApproval: false,
    isLeave: false,
    assignedUserIds: [] as number[],
  });

  const [selectedForms, setSelectedForms] = useState<Form[]>([]);
  const [showFormLibrary, setShowFormLibrary] = useState(false);
  const [showFormViewer, setShowFormViewer] = useState(false);
  const [viewingForm, setViewingForm] = useState<Form | null>(null);
  const [showFormEditor, setShowFormEditor] = useState(false);
  const [editingForm, setEditingForm] = useState<Form | null>(null);
  const [showNewFormBuilder, setShowNewFormBuilder] = useState(false);
  const [newFormBeingCreated, setNewFormBeingCreated] = useState<Form | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [attachments, setAttachments] = useState<GoogleDriveAttachment[]>([]);
  const [showDrivePicker, setShowDrivePicker] = useState(false);
  const [showDateFields, setShowDateFields] = useState(false);

  useEffect(() => {
    // Only load activity data if editing an existing activity (activity must have an id)
    if (activity?.id) {
      setFormData({
        title: activity.title || "",
        description: activity.description || "",
        startDate: activity.startDate || "",
        endDate: activity.endDate || "",
        requiresApproval: activity.requiresApproval ?? false,
        isLeave: activity.isLeave ?? false,
        assignedUserIds: activity.assignedUsers
          ? JSON.parse(activity.assignedUsers)
          : [],
      });

      // Show date fields if dates already exist
      if (activity.startDate || activity.endDate) {
        setShowDateFields(true);
      }

      // Extract forms from activityForms
      if (activity.activityForms) {
        const forms = activity.activityForms
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map((af) => af.form);
        setSelectedForms(forms);
      }

      // Load attachments from activity
      if (activity.attachments) {
        try {
          const attachmentsArray = JSON.parse(activity.attachments);
          setAttachments(attachmentsArray);
        } catch (e) {
        }
      }
    }

    // Load users on component mount or when user context is available
    loadUsers();
  }, [activity, user?.id]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFilesSelected = (files: GoogleDriveAttachment[]) => {
    setAttachments((prev) => [...prev, ...files]);
    setShowDrivePicker(false);
    toast.success(t("activities:filesAttached", { count: files.length }));
  };

  const handleRemoveAttachment = (fileId: string) => {
    setAttachments((prev) => prev.filter((att) => att.id !== fileId));
    toast.success(t("activities:attachmentRemoved"));
  };

  const handleFormSelect = (form: Form) => {
    if (selectedForms.find((f) => f.id === form.id)) {
      toast.error(t("activities:thisFormAlreadyAdded"));
      return;
    }

    setSelectedForms((prev) => [...prev, form]);
    toast.success(t("activities:formAddedToActivity", { title: form.title }));
  };

  const handleRemoveForm = (formId: number) => {
    setSelectedForms((prev) => prev.filter((f) => f.id !== formId));
    toast.success(t("activities:formRemovedFromActivity"));
  };

  const handleMoveForm = (formId: number, direction: "up" | "down") => {
    const currentIndex = selectedForms.findIndex((f) => f.id === formId);
    if (currentIndex === -1) return;

    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= selectedForms.length) return;

    const newForms = [...selectedForms];
    [newForms[currentIndex], newForms[newIndex]] = [
      newForms[newIndex],
      newForms[currentIndex],
    ];
    setSelectedForms(newForms);
  };

  const handleViewForm = (form: Form) => {
    setViewingForm(form);
    setShowFormViewer(true);
  };

  const handleCloseFormViewer = () => {
    setShowFormViewer(false);
    setViewingForm(null);
  };

  const handleEditFormInActivity = (form: Form) => {
    setEditingForm(form);
    setShowFormEditor(true);
  };

  const handleSaveEditedForm = async (updatedFormData: Partial<Form>) => {
    if (!editingForm) return;

    try {
      setLoading(true);

      // Save form to database
      const updatedForm = await apiService.updateForm(
        editingForm.id!,
        updatedFormData
      );

      // Update the form in selectedForms array
      setSelectedForms((prevForms) =>
        prevForms.map((f) => (f.id === editingForm.id ? updatedForm : f))
      );

      toast.success(t("forms:formUpdated") || "Form başarıyla güncellendi");
      setShowFormEditor(false);
      setEditingForm(null);
    } catch (error) {
      toast.error(t("forms:formUpdateError") || "Form güncellenemedi!");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelFormEditor = () => {
    setShowFormEditor(false);
    setEditingForm(null);
  };

  const handleSaveNewForm = async (form: Partial<Form>) => {
    try {
      setLoading(true);

      // Save form to database
      const savedForm = await apiService.createForm(form);

      // Add the newly created form to selected forms
      const formWithOrder = {
        ...savedForm,
        orderIndex: selectedForms.length,
      } as Form;

      setSelectedForms([...selectedForms, formWithOrder]);
      setShowNewFormBuilder(false);
      setNewFormBeingCreated(null);

      toast.success(
        t("forms:formCreatedAndSaved") ||
          "Form kaydedildi ve aktiviteye eklendi!"
      );
    } catch (error) {
      toast.error(t("forms:formSaveError") || "Form kaydedilemedi!");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelNewFormBuilder = () => {
    setShowNewFormBuilder(false);
    setNewFormBeingCreated(null);
    // Form oluşturmayı iptal edince library'yi açmıyoruz, sadece kapatıyoruz
  };

  const loadUsers = async () => {
    try {
      // includeSelf=true parametresi ile current user'ın da gelmesini sağlıyoruz
      const response = await axios.get("/api/users?includeSelf=true");
      if (response.data.success) {
        let usersList = response.data.data || [];

        // Current user'ı listenin en başına al
        if (user?.id) {
          usersList.sort((a: User, b: User) => {
            if (a.id === user.id) return -1; // Current user en başta
            if (b.id === user.id) return 1;
            return 0; // Diğer kullanıcılar olduğu gibi
          });
        }

        setUsers(usersList);

        // Yeni aktivite oluşturuluyorsa current user'ı otomatik ekle
        if (!activity && user?.id) {
          setFormData((prev) => {
            // Eğer zaten eklenmemişse ekle
            if (!prev.assignedUserIds.includes(user.id)) {
              return {
                ...prev,
                assignedUserIds: [...prev.assignedUserIds, user.id],
              };
            }
            return prev;
          });
        }
      }
    } catch (error) {
      toast.error(t("activities:errorLoadingUsers"));
    }
  };

  const handleUserToggle = (userId: number) => {
    setFormData((prev) => {
      const currentIds = prev.assignedUserIds;
      const newIds = currentIds.includes(userId)
        ? currentIds.filter((id) => id !== userId)
        : [...currentIds, userId];

      return {
        ...prev,
        assignedUserIds: newIds,
      };
    });
  };

  const removeUser = (userId: number) => {
    setFormData((prev) => ({
      ...prev,
      assignedUserIds: prev.assignedUserIds.filter((id) => id !== userId),
    }));
  };

  const getSelectedUsers = () => {
    return users.filter((user) => formData.assignedUserIds.includes(user.id));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error(t("activities:activityTitleRequired"));
      return;
    }

    setLoading(true);

    try {
      // Create activity object
      const activityData: Partial<ActivityDefinition> = {
        ...activity,
        ...formData,
        assignedUsers: JSON.stringify(formData.assignedUserIds), // Convert array to JSON string for backend
        attachments:
          attachments.length > 0 ? JSON.stringify(attachments) : undefined, // Add attachments
        activityForms: selectedForms.map((form, index) => ({
          id: 0, // Will be set by backend
          form: form,
          displayOrder: index,
          isRequired: true,
          isVisible: true,
          conditionExpression: undefined,
        })),
      };

      onSave(activityData as ActivityDefinition);
    } catch (error) {
      toast.error(t("activities:errorSavingActivity"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Full Screen Modal Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-blue-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <span className="text-2xl">🎯</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {readonly
                      ? t("activities:viewActivity")
                      : activity
                      ? t("activities:editActivity")
                      : t("activities:createNewActivity")}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {readonly
                      ? t("activities:viewActivityDescription")
                      : activity
                      ? t("activities:editActivityDescription")
                      : t("activities:createActivityDescription")}
                  </p>
                </div>
              </div>
              <button
                onClick={onCancel}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto">
            <div className="p-8">
              {/* Info Message for Managers */}
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <span className="text-2xl">💡</span>
                  </div>
                  <div>
                    <p className="text-sm text-blue-800 font-medium">
                      {t("activities:durationInfoMessage")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Basic Information */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {t("activities:basicInformation")}
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("activities:activityTitle")} *
                        </label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) =>
                            handleInputChange("title", e.target.value)
                          }
                          disabled={readonly}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                          placeholder={t("activities:enterActivityTitle")}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("common:description")}
                        </label>
                        <textarea
                          value={formData.description}
                          onChange={(e) =>
                            handleInputChange("description", e.target.value)
                          }
                          disabled={readonly}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                          placeholder={t("activities:describeActivity")}
                        />
                      </div>

                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={formData.requiresApproval}
                          onChange={(e) =>
                            handleInputChange("requiresApproval", e.target.checked)
                          }
                          disabled={readonly}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                        />
                        <span>{t("activities:requiresApproval")}</span>
                      </label>

                      <label className="flex items-start gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={formData.isLeave}
                          onChange={(e) =>
                            handleInputChange("isLeave", e.target.checked)
                          }
                          disabled={readonly}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                        />
                          <span>
                          <span className="font-medium">
                            Bu bir izin aktivitesidir.
                          </span>{" "}
                          <span className="text-gray-500">
                            (Çalışılan saatlerden çıkarılır)
                          </span>
                        </span>
                      </label>

                      {!showDateFields ? (
                        <button
                          type="button"
                          onClick={() => setShowDateFields(true)}
                          disabled={readonly}
                          className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <CalendarIcon className="h-4 w-4 mr-2" />
                          {t("activities:addDates")}
                        </button>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700">
                              {t("activities:dates")}
                            </label>
                            {!readonly && (
                              <button
                                type="button"
                                onClick={() => {
                                  setShowDateFields(false);
                                  handleInputChange("startDate", "");
                                  handleInputChange("endDate", "");
                                }}
                                className="text-xs text-gray-500 hover:text-gray-700"
                              >
                                {t("activities:removeDates")}
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("activities:startDate")}
                              </label>
                              <input
                                type="date"
                                value={formData.startDate}
                                onChange={(e) =>
                                  handleInputChange("startDate", e.target.value)
                                }
                                disabled={readonly}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("activities:endDate")}
                              </label>
                              <input
                                type="date"
                                value={formData.endDate}
                                onChange={(e) =>
                                  handleInputChange("endDate", e.target.value)
                                }
                                disabled={readonly}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t("activities:authorizedUsers")}
                        </label>

                        {/* Available Users List with Checkboxes */}
                        {!readonly && (
                          <div className="mb-4">
                            <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto bg-white">
                              {users.map((availableUser) => {
                                const isCurrentUser =
                                  availableUser.id === user?.id;
                                const isAssigned =
                                  formData.assignedUserIds.includes(
                                    availableUser.id
                                  );
                                const isChecked = isAssigned;

                                return (
                                  <label
                                    key={availableUser.id}
                                    htmlFor={`user-${availableUser.id}`}
                                    className={`flex items-center p-3 border-b border-gray-100 last:border-b-0 ${
                                      isCurrentUser
                                        ? "bg-green-50 hover:bg-green-100 cursor-pointer"
                                        : "hover:bg-gray-50 cursor-pointer"
                                    }`}
                                  >
                                    <input
                                      id={`user-${availableUser.id}`}
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() =>
                                        handleUserToggle(availableUser.id)
                                      }
                                      disabled={false}
                                      className="w-4 h-4 border-gray-300 rounded focus:ring-blue-500"
                                      aria-label={`${availableUser.firstName} ${availableUser.lastName}`}
                                    />
                                    <div className="ml-3 flex items-center flex-1">
                                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                                        {availableUser.firstName.charAt(0)}
                                        {availableUser.lastName.charAt(0)}
                                      </div>
                                      <div className="ml-3">
                                        <div className="text-sm font-medium text-gray-900">
                                          {availableUser.firstName}{" "}
                                          {availableUser.lastName}
                                          {isCurrentUser && " (Siz)"}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          @{availableUser.username}
                                        </div>
                                      </div>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Selected Users Summary Section */}
                        {!readonly && getSelectedUsers().length > 0 && (
                          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex flex-wrap gap-2">
                              {getSelectedUsers().map((selectedUser) => {
                                const isCurrentUser =
                                  selectedUser.id === user?.id;
                                return (
                                  <div
                                    key={selectedUser.id}
                                    className={`inline-flex items-center px-3 py-1.5 text-white text-sm rounded-lg font-medium ${
                                      isCurrentUser
                                        ? "bg-green-600"
                                        : "bg-blue-600"
                                    }`}
                                  >
                                    <UserIcon className="w-4 h-4 mr-1.5" />
                                    <span>
                                      {selectedUser.firstName}{" "}
                                      {selectedUser.lastName}
                                      {isCurrentUser && " (Siz)"}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeUser(selectedUser.id)
                                      }
                                      className="ml-2 hover:bg-blue-800 rounded p-0.5"
                                    >
                                      <XMarkIcon className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {readonly && getSelectedUsers().length === 0 && (
                          <div className="text-sm text-gray-500 italic">
                            {t("activities:noUsersAssigned")}
                          </div>
                        )}

                        {readonly && getSelectedUsers().length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {getSelectedUsers().map((selectedUser) => (
                              <div
                                key={selectedUser.id}
                                className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg font-medium"
                              >
                                <UserIcon className="w-4 h-4 mr-1.5" />
                                <span>
                                  {selectedUser.firstName}{" "}
                                  {selectedUser.lastName}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Forms and Attachments */}
                <div className="space-y-6">
                  {/* Forms Configuration */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {t("activities:activityForms")} ({selectedForms.length})
                      </h3>
                      {!readonly && (
                        <button
                          onClick={() => {
                            setNewFormBeingCreated({
                              id: 0,
                              title: "",
                              description: "",
                              formStructure: { elements: [], elementOrder: [] },
                              createdBy: user?.id || 0,
                              isPublic: false,
                              isArchived: false,
                              version: 1,
                              createdAt: new Date().toISOString(),
                              updatedAt: new Date().toISOString(),
                            });
                            setShowNewFormBuilder(true);
                          }}
                          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <span>+</span>
                          <span>{t("activities:addForm")}</span>
                        </button>
                      )}
                    </div>

                    {selectedForms.length === 0 ? (
                      <div className="text-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 mb-6">
                        <DocumentTextIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">
                          {t("activities:noFormsAddedYet")}
                        </p>
                        <p className="text-xs text-gray-400">
                          {t("activities:clickAddFormToAddFirst")}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto mb-6">
                        {selectedForms.map((form, index) => (
                          <div
                            key={form.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                          >
                            <div className="flex items-center space-x-3">
                              <DocumentTextIcon className="w-5 h-5 text-blue-500" />
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {form.title}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {form.formStructure.elements.length}{" "}
                                  {t("activities:fields")} •
                                  {t("activities:order")}: {index + 1}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-1 ml-4">
                              <button
                                onClick={() => handleViewForm(form)}
                                className="p-1 text-gray-400 hover:text-green-600"
                                title={t("activities:viewForm")}
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              {!readonly && (
                                <>
                                  <button
                                    onClick={() =>
                                      handleEditFormInActivity(form)
                                    }
                                    className="p-1 text-gray-400 hover:text-blue-600"
                                    title={t("activities:editForm")}
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleMoveForm(form.id, "up")
                                    }
                                    disabled={index === 0}
                                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={t("activities:moveUp")}
                                  >
                                    <ChevronUpIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleMoveForm(form.id, "down")
                                    }
                                    disabled={
                                      index === selectedForms.length - 1
                                    }
                                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={t("activities:moveDown")}
                                  >
                                    <ChevronDownIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleRemoveForm(form.id)}
                                    className="p-1 text-gray-400 hover:text-red-600"
                                    title={t("activities:removeForm")}
                                  >
                                    <XMarkIcon className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Attachments Section - Moved Below Forms */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-base font-semibold text-gray-800 flex items-center space-x-2">
                        <PaperClipIcon className="w-5 h-5 text-green-600" />
                        <span>{t("activities:attachedDocuments")}</span>
                      </label>
                      {!readonly && (
                        <button
                          type="button"
                          onClick={() => setShowDrivePicker(true)}
                          className="flex items-center space-x-2 text-sm text-green-600 hover:text-green-700 font-medium bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          <PlusIcon className="w-4 h-4" />
                          <span>{t("activities:addFromGoogleDrive")}</span>
                        </button>
                      )}
                    </div>

                    {!readonly && (
                      <div className="flex items-center justify-center w-full mb-4">
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-green-300 border-dashed rounded-xl cursor-pointer bg-green-50 hover:bg-green-100 transition-colors">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <svg
                              className="w-8 h-8 mb-3 text-green-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                              />
                            </svg>
                            <p className="mb-2 text-sm text-gray-500">
                              <span className="font-semibold">
                                {t("activities:clickToUpload") || "Dosya yüklemek için tıklayın"}
                              </span>
                            </p>
                            <p className="text-xs text-gray-500">
                              {t("activities:uploadFileTypes") || "PDF, DOC, XLS, JPG, PNG"}
                            </p>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            onChange={async (e) => {
                              if (e.target.files && e.target.files[0]) {
                                const file = e.target.files[0];
                                const loadingToast = toast.loading(
                                  t("activities:uploading")
                                );
                                try {
                                  const uploadedFile =
                                    await apiService.uploadTemplateFile(file);

                                  const newAttachment: GoogleDriveAttachment = {
                                    id: uploadedFile.id,
                                    name: uploadedFile.name,
                                    url: uploadedFile.url,
                                    mimeType: uploadedFile.mimeType,
                                    iconUrl: uploadedFile.iconUrl,
                                    thumbnailUrl: uploadedFile.thumbnailUrl,
                                    addedAt: new Date().toISOString(),
                                  };

                                  setAttachments([
                                    ...attachments,
                                    newAttachment,
                                  ]);
                                  toast.success(t("activities:uploadSuccess"), {
                                    id: loadingToast,
                                  });
                                } catch (error) {
                                  toast.error(t("activities:uploadError"), {
                                    id: loadingToast,
                                  });
                                }
                              }
                            }}
                          />
                        </label>
                      </div>
                    )}

                    {attachments.length > 0 ? (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-700">
                          {t("activities:attachedFiles")} ({attachments.length})
                        </h4>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {attachments.map((att) => (
                            <div
                              key={att.id}
                              className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200 hover:border-green-300 hover:shadow-sm transition-all"
                            >
                              <div className="flex items-center space-x-3 flex-1 min-w-0">
                                {att.iconUrl ? (
                                  <img
                                    src={att.iconUrl}
                                    alt=""
                                    className="w-8 h-8 object-contain"
                                  />
                                ) : (
                                  <DocumentTextIcon className="w-8 h-8 text-blue-500" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const previewWindow =
                                        apiService.shouldOpenPreviewWindow(att.name, att.mimeType)
                                          ? window.open("", "_blank")
                                          : null;
                                      if (previewWindow) {
                                        previewWindow.opener = null;
                                        apiService.preparePreviewWindow(previewWindow, att.name);
                                      }
                                      void apiService
                                        .previewGoogleDriveFile(att.id, att.name, previewWindow)
                                        .catch(() => toast.error("Dosya görüntülenemedi."));
                                    }}
                                    className="text-sm font-medium text-blue-600 hover:underline truncate block text-left"
                                  >
                                    {att.name}
                                  </button>
                                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                                    <span>
                                      {t("activities:added")}:{" "}
                                      {new Date(att.addedAt).toLocaleDateString(
                                        "tr-TR"
                                      )}
                                    </span>
                                    {att.mimeType === "application/vnd.google-apps.folder" && (
                                      <span className="bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded text-[10px]">
                                        Folder
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {!readonly && (
                                <button
                                  onClick={() => handleRemoveAttachment(att.id)}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors ml-2"
                                  title={t("activities:removeAttachment")}
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      readonly && (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                          <DocumentTextIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-sm text-gray-500">
                            {t("activities:noDocumentsAttachedYet")}
                          </p>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          {!readonly && (
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {loading && (
                    <span className="flex items-center text-blue-600 text-sm">
                      <div className="w-2 h-2 bg-blue-400 rounded-full mr-2 animate-pulse"></div>
                      {t("activities:saving")}
                    </span>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={onCancel}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    {t("common:cancel")}
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <span>💾</span>
                    <span>
                      {loading
                        ? t("activities:saving")
                        : t("activities:saveActivity")}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Form Viewer Modal */}
      {showFormViewer && viewingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl h-[90vh] flex flex-col shadow-2xl">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <span className="text-2xl">👁️</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {viewingForm.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t("activities:formPreview")}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseFormViewer}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <FormViewer
                formData={{
                  formName: viewingForm.title,
                  formDescription: viewingForm.description || "",
                  elements: viewingForm.formStructure.elements,
                  elementOrder: JSON.stringify(
                    viewingForm.formStructure.elementOrder
                  ),
                  submissionData:
                    viewingForm.submissionData ||
                    viewingForm.formStructure.elements.reduce(
                      (acc, element) => {
                        // Use the actual element value if it exists, otherwise use placeholder or empty
                        if (element.value) {
                          acc[element.id] = element.value;
                        } else if (element.placeholder) {
                          acc[element.id] = element.placeholder;
                        } else {
                          acc[element.id] = "";
                        }
                        return acc;
                      },
                      {} as Record<string, any>
                    ),
                }}
                title={viewingForm.title}
              />
            </div>
          </div>
        </div>
      )}

      {/* Form Library Modal */}
      {showFormLibrary && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl w-full max-w-6xl h-5/6 flex flex-col shadow-2xl">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-pink-50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <span className="text-2xl">📚</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {t("activities:selectFromLibrary")}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t("activities:selectExistingForm")}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowFormLibrary(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden p-6 bg-gray-50">
              <div className="h-full bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <FormLibrary
                  selectionMode={true}
                  onSelectForm={handleFormSelect}
                  multiSelect={false}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Editor Modal */}
      {showFormEditor && editingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
          <div className="w-full h-full">
            <FormEditor
              form={editingForm}
              onSave={handleSaveEditedForm}
              onCancel={handleCancelFormEditor}
              readonly={false}
            />
          </div>
        </div>
      )}

      {/* New Form Builder Modal */}
      {showNewFormBuilder && newFormBeingCreated && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
          <div className="w-full h-full">
            <FormEditor
              form={newFormBeingCreated}
              onSave={handleSaveNewForm}
              onCancel={handleCancelNewFormBuilder}
              readonly={false}
              showLibraryButton={true}
              onShowLibrary={() => {
                setShowFormLibrary(true);
                setShowNewFormBuilder(false);
              }}
            />
          </div>
        </div>
      )}

      {/* Google Drive File Picker Modal */}
      {showDrivePicker && (
        <GoogleDriveFilePicker
          isOpen={showDrivePicker}
          onClose={() => setShowDrivePicker(false)}
          onSelect={handleFilesSelected}
        />
      )}
    </>
  );
};
